import React, { useEffect, useState } from "react";
import api from "../../api";
import { Card, CardBody, Col, Container, Row } from "shards-react";
import {
  Button,
  CircularProgress,
  createMuiTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MuiThemeProvider,
  Snackbar,
  Tooltip
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import Fab from "@material-ui/core/Fab";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import FolderIcon from "@material-ui/icons/Folder";
import DeleteIcon from "@material-ui/icons/Delete";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";

const getMuiTheme = () =>
  createMuiTheme({
    overrides: {
      MuiTableCell: {
        root: {
          padding: "0px 8px 0px 8px",
          fontSize: "0.73rem"
        }
      },
      MuiFormLabel: {
        root: {
          fontSize: "0.75rem"
        }
      },
      MuiInputBase: {
        root: {
          fontSize: "0.75rem"
        }
      },
      MuiTypography: {
        body1: {
          fontSize: "0.75rem"
        }
      }
    }
  });

const unwrap = data => {
  if (data && Object.prototype.hasOwnProperty.call(data, "data")) {
    return data.data;
  }
  return data;
};

const formatDate = value => {
  if (!value) return "";
  const date = String(value).slice(0, 10);
  const parts = date.split("-");
  if (parts.length !== 3) return value;
  return parts[2] + "/" + parts[1] + "/" + parts[0];
};

const statusLabel = value => {
  if (value === "draft") return "Borrador";
  if (value === "published") return "Publicada";
  if (value === "archived") return "Archivada";
  return value || "";
};

const todayIso = () => {
  const date = new Date();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return (
    date.getFullYear() +
    "-" +
    (month < 10 ? "0" : "") +
    month +
    "-" +
    (day < 10 ? "0" : "") +
    day
  );
};

const addDaysIso = (iso, days) => {
  const parts = iso.split("-");
  const date = new Date(
    Number(parts[0]),
    Number(parts[1]) - 1,
    Number(parts[2])
  );
  date.setDate(date.getDate() + days);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return (
    date.getFullYear() +
    "-" +
    (month < 10 ? "0" : "") +
    month +
    "-" +
    (day < 10 ? "0" : "") +
    day
  );
};

const dateOnly = value => (value ? String(value).slice(0, 10) : "");

const isCurrent = (item, today) => {
  if (item.status !== "published") return false;
  const from = dateOnly(item.valid_from);
  const to = dateOnly(item.valid_to);
  if (from && from > today) return false;
  if (to && to < today) return false;
  return true;
};

const summarizeLists = lists => {
  const today = todayIso();
  const limit = addDaysIso(today, 30);
  const summary = {
    total: lists.length,
    published: 0,
    draft: 0,
    archived: 0,
    current: 0,
    expiring: 0,
    expired: 0
  };

  lists.forEach(item => {
    if (item.status === "published") summary.published += 1;
    if (item.status === "draft") summary.draft += 1;
    if (item.status === "archived") summary.archived += 1;
    if (isCurrent(item, today)) summary.current += 1;
    const to = dateOnly(item.valid_to);
    if (item.status === "published" && to && to < today) summary.expired += 1;
    if (item.status === "published" && to && to >= today && to <= limit) {
      summary.expiring += 1;
    }
  });

  return summary;
};

const Kpi = ({ label, value, hint }) => (
  <Col md="3" sm="6" className="mb-3">
    <Card className="h-100 mb-0">
      <CardBody className="py-3">
        <div style={{ fontSize: "0.7rem", color: "#6c757d" }}>{label}</div>
        <div style={{ fontSize: "1.35rem", fontWeight: 600, lineHeight: 1.2 }}>
          {value}
        </div>
        {hint ? (
          <div style={{ fontSize: "0.7rem", color: "#6c757d" }}>{hint}</div>
        ) : null}
      </CardBody>
    </Card>
  </Col>
);

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const FlexPriceLists = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const getLists = async () => {
    const token = localStorage.getItem("userToken");
    return axios
      .get(api.flexPriceLists().index(), {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        const data = unwrap(res.data);
        setLists(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        setLists([]);
        setLoading(false);
        setSnackbar({
          open: true,
          message: "No se pudieron cargar las listas de precios",
          severity: "error"
        });
      });
  };

  useEffect(() => {
    getLists();
  }, []);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const token = localStorage.getItem("userToken");
    setDeleting(true);
    try {
      await axios.delete(api.flexPriceLists().delete(pendingDelete.id), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingDelete(null);
      setSnackbar({
        open: true,
        message: "Lista eliminada",
        severity: "success"
      });
      setLoading(true);
      await getLists();
    } catch (err) {
      console.log(err);
      setSnackbar({
        open: true,
        message: "No se pudo eliminar la lista",
        severity: "error"
      });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      name: "id",
      label: "Id",
      options: {
        filter: false,
        sort: true,
        display: true
      }
    },
    {
      name: "name",
      label: "Nombre",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        display: true
      }
    },
    {
      name: "status",
      label: "Estado",
      options: {
        filter: true,
        filterType: "textField",
        sort: true,
        display: true,
        customBodyRender: value => statusLabel(value)
      }
    },
    {
      name: "valid_from",
      label: "Vigencia desde",
      options: {
        filter: false,
        sort: true,
        display: true,
        customBodyRender: value => formatDate(value)
      }
    },
    {
      name: "valid_to",
      label: "Vigencia hasta",
      options: {
        filter: false,
        sort: true,
        display: true,
        customBodyRender: value => formatDate(value)
      }
    },
    {
      name: "used_max_year",
      label: "Año máximo usado",
      options: {
        filter: false,
        sort: true,
        display: true
      }
    },
    {
      name: "Acciones",
      label: "",
      options: {
        filter: false,
        sort: false,
        setCellHeaderProps: () => ({ align: "center" }),
        customBodyRender: (value, tableMeta) => {
          const rowId = tableMeta.rowData[0];
          const row = lists.find(item => item.id === rowId);
          if (!rowId) return "";
          return (
            <div className="row" style={{ display: "block" }}>
              <Tooltip title="Editar">
                <Fab
                  color="primary"
                  aria-label="edit"
                  variant="extended"
                  style={{ boxShadow: "none", backgroundColor: "green" }}
                  className="MuiButtonBase-root MuiFab-sizeSmall mr-2"
                  href={"/bookings/flex-price-lists/" + rowId}
                >
                  <EditIcon fontSize="small" />
                </Fab>
              </Tooltip>
              <Tooltip title="Stock">
                <Fab
                  aria-label="stock"
                  variant="extended"
                  style={{ boxShadow: "none", backgroundColor: "#1565c0" }}
                  className="MuiButtonBase-root MuiFab-sizeSmall mr-2"
                  href={"/bookings/flex-price-lists/" + rowId + "/stock"}
                >
                  <DirectionsCarIcon fontSize="small" style={{ color: "#fff" }} />
                </Fab>
              </Tooltip>
              <Tooltip title="Eliminar">
                <IconButton
                  aria-label="delete"
                  onClick={() =>
                    setPendingDelete({
                      id: rowId,
                      name: row ? row.name : rowId
                    })
                  }
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          );
        }
      }
    }
  ];

  const summary = summarizeLists(lists);

  const options = {
    print: false,
    responsive: "stacked",
    selectableRowsOnClick: false,
    selectableRows: false,
    download: false,
    textLabels: {
      toolbar: {
        search: "Buscar",
        viewColumns: "Ver columnas",
        filterTable: "Filtros",
        downloadCsv: "Exportar XLS"
      },
      body: {
        noMatch: loading ? <CircularProgress /> : "No hay resultados"
      },
      viewColumns: {
        title: "Ver columnas"
      },
      pagination: {
        next: "Siguiente",
        previous: "Anterior",
        rowsPerPage: "Cantidad por página:",
        displayRows: "de"
      },
      filter: {
        all: "Todos",
        title: "Filtros",
        reset: "Limpiar"
      }
    },
    customToolbar: () => {
      return (
        <React.Fragment>
          <Tooltip title={"Nueva"}>
            <IconButton
              onClick={() => {
                window.location.href = "/bookings/flex-price-lists/new";
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={"Importar lista"}>
            <IconButton
              onClick={() => {
                window.location.href = "/bookings/flex-price-lists/import";
              }}
            >
              <FolderIcon />
            </IconButton>
          </Tooltip>
        </React.Fragment>
      );
    }
  };

  return (
    <div>
      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        className="alert-fix"
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
      >
        <DialogTitle>Eliminar lista</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Eliminar la lista{" "}
            {pendingDelete ? pendingDelete.name : ""}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDelete(null)}
            disabled={deleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDelete}
            color="secondary"
            disabled={deleting}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      {loading ? (
        <div>
          <CircularProgress />
        </div>
      ) : (
        <Container fluid className="main-content-container px-4">
          <Row className="pt-4">
            <Kpi
              label="Listas"
              value={summary.total}
              hint={summary.draft + " en borrador"}
            />
            <Kpi
              label="Publicadas"
              value={summary.published}
              hint={
                summary.archived +
                (summary.archived === 1 ? " archivada" : " archivadas")
              }
            />
            <Kpi
              label="Vigentes"
              value={summary.current}
              hint={
                summary.expired +
                (summary.expired === 1 ? " vencida" : " vencidas")
              }
            />
            <Kpi
              label="Por vencer"
              value={summary.expiring}
              hint="Publicadas, en los próximos 30 días"
            />
          </Row>
          <Row>
            <Col className="p-2">
              <MuiThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                  title="Listas de Precios Flex"
                  data={lists}
                  columns={columns}
                  options={options}
                />
              </MuiThemeProvider>
            </Col>
          </Row>
        </Container>
      )}
    </div>
  );
};

export default FlexPriceLists;
